import * as React from 'react';
import { useHistory } from 'react-router';
import {
  useUpdateCustomerMutation,
  useGetCustomerLazyQuery,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ToastContext } from '../components/Toast';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { FormActions } from '../components/FormActions';
import { PageHeader } from '../components/PageHeader';
import { Input } from '../components/Input';
import { useValidation } from '../utils/Validation';
import { ValidationRules } from '../utils/ValidationRules';
import './AdminCreateCustomerPage.css';

/** お客様一覧のパス。キャンセルと保存の後にこの一覧へ戻る */
const LIST_PATH = '/admin/clients';

interface AdminCreateCustomerPageProps {
  existingId?: number;
}

interface CustomerFormTexts {
  title: string;
  submitLabel: string;
  completedMessage: string;
  failedMessage: string;
}

/** 新規登録と編集で、見出し・送信ボタン・通知の文言を出し分ける */
function getFormTexts(isNew: boolean): CustomerFormTexts {
  if (isNew) {
    return {
      title: 'お客様の登録',
      submitLabel: '登録',
      completedMessage: 'お客様を登録しました',
      failedMessage: 'お客様の登録に失敗しました',
    };
  }
  return {
    title: 'お客様の編集',
    submitLabel: '保存',
    completedMessage: 'お客様を保存しました',
    failedMessage: 'お客様の保存に失敗しました',
  };
}

export const AdminCreateCustomerPage: React.FC<AdminCreateCustomerPageProps> = function (
  props
) {
  const isNew = !props.existingId;
  const texts = getFormTexts(isNew);

  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const validation = useValidation(ValidationRules.Customer);
  const [name, setName] = validation.useState('name', '');
  const [updateCustomer, updateCustomerResult] = useUpdateCustomerMutation({
    onCompleted() {
      toastContext.showToast(texts.completedMessage, 'success');
      history.push(LIST_PATH);
    },
    onError() {
      toastContext.showToast(texts.failedMessage, 'error');
    },
  });
  const [getCustomer, getCustomerResult] = useGetCustomerLazyQuery({
    onCompleted(data) {
      const customer = data.getCustomer;
      setName(customer.name);
    },
  });

  if (props.existingId) {
    if (!getCustomerResult.called) {
      getCustomer({
        variables: {
          customerId: props.existingId,
        },
      });
    }

    if (getCustomerResult.error) {
      return <GraphQLErrorDialog error={getCustomerResult.error} />;
    }

    if (getCustomerResult.loading || !getCustomerResult.data) {
      return <Loading />;
    }
  }

  return (
    <div className="AdminCreateCustomerPage">
      <PageHeader
        title={texts.title}
        breadcrumbs={[
          { label: '管理画面' },
          { label: 'お客様一覧', to: LIST_PATH },
          { label: texts.title },
        ]}
      />
      <div className="AdminCreateCustomerPage-form">
        <Input
          label="お客様名"
          type="text"
          required={true}
          onChange={(event) => {
            const name = event.target.value;
            setName(name);
          }}
          value={name}
          validationMessage={validation.get('name')}
        />
        <FormActions
          submitLabel={texts.submitLabel}
          submitDisabled={validation.isInvalid || updateCustomerResult.loading}
          onSubmit={() => {
            updateCustomer({
              variables: {
                input: {
                  id: props.existingId,
                  name: name,
                },
              },
            });
          }}
          onCancel={() => history.push(LIST_PATH)}
        />
      </div>
    </div>
  );
};
